/**
 * SPRINT V4.3 — Premium Goal Celebration.
 * Listens to `vs:v4:premium-goal` events. Fullscreen overlay with
 * confetti pulse, optional vibration, optional sound, and a faux
 * "celebrating now" counter.
 *
 * P0-01 LAUNCH BLOCKER — keep share affordance visible long enough
 * for users to react: minimum 25s, manual close, sticky-on-interaction.
 */
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { celebrate } from "@/lib/pr32/celebrationEngine";
import { trackEvent } from "@/lib/audit";
import { getNationFlag, getNationName } from "@/lib/share/nationWarsShare";
import { sponsorFor } from "@/lib/sponsor/sponsorMoments";
import { SponsorMomentBadge } from "@/components/sponsor/SponsorMomentBadge";
import { fireVictoryPrompt } from "@/lib/viral/victoryPromptBus";
import { shareVictory } from "@/lib/whatsappViral/whatsappEngine";
import { useLanguage } from "@/hooks/useLanguage";

export const PREMIUM_GOAL_EVENT = "vs:v4:premium-goal";

export interface PremiumGoalDetail {
  nationCode?: string | null;
  teamLabel?: string | null;
  sound?: boolean;
}

export function firePremiumGoal(detail: PremiumGoalDetail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<PremiumGoalDetail>(PREMIUM_GOAL_EVENT, { detail }));
}

export function PremiumGoalCelebration() {
  const [state, setState] = useState<{ detail: PremiumGoalDetail; celebrators: number } | null>(null);
  const [stuck, setStuck] = useState(false);
  const { lang } = useLanguage();
  const dismissTimer = useRef<number | null>(null);
  const promptTimer = useRef<number | null>(null);

  const MIN_VISIBLE_MS = 25_000;

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PremiumGoalDetail>).detail ?? {};
      const celebrators = 1200 + Math.floor(Math.random() * 8800);
      setState({ detail, celebrators });
      setStuck(false);
      void trackEvent("goal_celebration_premium", {
        nation: detail.nationCode ?? null, team: detail.teamLabel ?? null, celebrators,
      });
      void trackEvent("goal_share_visible", {
        nation: detail.nationCode ?? null, team: detail.teamLabel ?? null,
      });
      try { celebrate("points_won", { vibrate: true, sound: !!detail.sound }); } catch { /* noop */ }
    };
    window.addEventListener(PREMIUM_GOAL_EVENT, handler);
    return () => window.removeEventListener(PREMIUM_GOAL_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!state) return;
    if (stuck) return; // user interacted — keep visible until manual close
    dismissTimer.current = window.setTimeout(() => {
      void trackEvent("goal_share_dismissed", { reason: "auto" });
      setState(null);
    }, MIN_VISIBLE_MS);
    promptTimer.current = window.setTimeout(
      () => fireVictoryPrompt({ source: "premium_goal" }),
      2400,
    );
    return () => {
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
      if (promptTimer.current) window.clearTimeout(promptTimer.current);
    };
  }, [state, stuck]);

  const cancelAutoDismiss = () => {
    if (dismissTimer.current) {
      window.clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }
    setStuck(true);
  };

  const close = () => {
    void trackEvent("goal_share_dismissed", { reason: "manual" });
    setState(null);
  };

  const doShare = async (variant: "goal" | "play" | "reaction") => {
    cancelAutoDismiss();
    void trackEvent("goal_share_clicked", { variant });
    const momentLabel =
      variant === "goal" ? "GOL" : variant === "play" ? "JUGADAZA" : "REACCIÓN";
    await shareVictory(
      {
        momentKind: "goal",
        momentLabel,
        matchLabel: state?.detail.teamLabel ?? null,
        nationCode: state?.detail.nationCode ?? null,
      },
      {
        source: `premium_goal_${variant}`,
        lang,
        campaignOverride: "premium_goal",
        legacyEvents: {
          native: "match_share_native",
          whatsapp: "match_share_native",
          copy: "match_share_copy",
          payload: { match_id: "live", moment: "goal", variant },
        },
      },
    );
  };

  if (!state) return null;
  const { detail, celebrators } = state;
  const flag = getNationFlag(detail.nationCode ?? null);
  const label = detail.teamLabel ?? getNationName(detail.nationCode ?? null);
  const sponsor = sponsorFor("last_minute_drama");

  const shareLabels = {
    ES: { goal: "Compartir Gol", play: "Compartir Jugada", reaction: "Compartir Reacción", close: "Cerrar" },
    EN: { goal: "Share Goal", play: "Share Play", reaction: "Share Reaction", close: "Close" },
    PT: { goal: "Compartilhar Gol", play: "Compartilhar Jogada", reaction: "Compartilhar Reação", close: "Fechar" },
  }[lang];

  return (
    <div
      role="alert"
      aria-label="¡GOL!"
      className="fixed inset-0 z-[110] flex flex-col items-center justify-center bg-gradient-to-b from-primary/30 via-background/80 to-background animate-fade-in"
      onMouseEnter={cancelAutoDismiss}
      onTouchStart={cancelAutoDismiss}
    >
      <button
        type="button"
        onClick={close}
        aria-label={shareLabels.close}
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-background/70 text-foreground shadow-md backdrop-blur hover:bg-background"
      >
        <X className="h-5 w-5" />
      </button>
      <div className="text-[24vw] leading-none drop-shadow-2xl animate-scale-in">{flag}</div>
      <div className="mt-2 text-5xl font-black uppercase tracking-widest text-primary drop-shadow-lg">
        ¡GOL!
      </div>
      <div className="mt-1 text-lg font-bold">{label}</div>
      <div className="mt-3 rounded-full border border-primary/50 bg-background/60 px-4 py-1 text-xs font-bold tabular-nums">
        🎉 {celebrators.toLocaleString()} celebrando ahora
      </div>
      {sponsor && (
        <div className="mt-3">
          <SponsorMomentBadge sponsor={sponsor} />
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2 px-4">
        <button
          type="button"
          onClick={() => doShare("goal")}
          className="rounded-full bg-primary px-4 py-2 text-xs font-black uppercase tracking-wider text-primary-foreground shadow-lg hover:opacity-90"
        >
          ⚽ {shareLabels.goal}
        </button>
        <button
          type="button"
          onClick={() => doShare("play")}
          className="rounded-full border border-primary/60 bg-background/70 px-4 py-2 text-xs font-black uppercase tracking-wider text-foreground hover:bg-background"
        >
          🎬 {shareLabels.play}
        </button>
        <button
          type="button"
          onClick={() => doShare("reaction")}
          className="rounded-full border border-primary/60 bg-background/70 px-4 py-2 text-xs font-black uppercase tracking-wider text-foreground hover:bg-background"
        >
          🔥 {shareLabels.reaction}
        </button>
      </div>
    </div>
  );
}